import React from 'react';
import { usePortfolioKPIs } from '../../../contexts/PortfolioKPIContext';
import { formatCurrency, formatSignedCurrency } from '../../../utils/formatters';
import { isMarketOpen, getLastTradingDay } from '../../../utils/marketHours';
import { VerdictCounts } from '../../../utils/disciplineVerdict';
import { LedgerFilter } from '../../../utils/positionLedger';
import PortfolioBriefButton from '../PortfolioBriefButton';
import LatestPriceChip from '../../common/LatestPriceChip';
import { LatestPositionsResponse } from '../../../types/latestPrice';

interface Props {
  total: number;
  counts: VerdictCounts | null;
  filter: LedgerFilter;
  setFilter: (f: LedgerFilter) => void;
  search: string;
  setSearch: (s: string) => void;
  onBrief: () => void;
  briefLoading: boolean;
  /** Latest P&L on the latest delayed price (server totals = sum of its rows). */
  latest?: LatestPositionsResponse | null;
}

const sign = (v: number | null | undefined) => (v == null ? '' : v > 0 ? 'ck-gain' : v < 0 ? 'ck-loss' : '');
const money = (v: number | null | undefined) => (v == null ? '—' : formatCurrency(v));
const signed = (v: number | null | undefined) => (v == null ? '—' : formatSignedCurrency(v));
/** "2 not priced" when a header leaves rows out because their value is unknown. */
const missing = (n: number | undefined) => (n ? <small className="ph-miss" title="Positions without a mark are left out of this total, never counted as 0">{n} not priced</small> : null);

/**
 * Positions page header. Every figure is a gold.portfolio_current column, the
 * sum of the rows below by construction (gold v2 phase 3b); the browser only
 * formats. Labels per the glossary.
 */
const PositionsHeader: React.FC<Props> = ({ total, counts, filter, setFilter, search, setSearch, onBrief, briefLoading, latest }) => {
  const { kpis } = usePortfolioKPIs();
  const s = kpis.summary;
  const open = isMarketOpen();
  const last = getLastTradingDay();
  const asOf = last ? new Date(last + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null;
  // Shown only outside the regular session, or when the latest price has moved
  // away from the 15-min Price. Secondary: the 15-min figure stays the headline.
  const lt = latest?.totals;
  const showLatest = !!lt && lt.latest_today_pnl != null && (latest!.session !== 'regular' || !!lt.differs_from_price);

  const chips: Array<{ key: LedgerFilter; label: string; n: number | null }> = [
    { key: 'all', label: `All ${total}`, n: null },
    { key: 'risk', label: 'protective stops', n: counts?.risk ?? 0 },
    { key: 'clock', label: 'time stop', n: counts?.clock ?? 0 },
    { key: 'goal', label: 'profit target', n: counts?.goal ?? 0 },
    { key: 'quiet', label: 'no rule fired', n: counts?.quiet ?? 0 },
  ];

  return (
    <div className="ph">
      <div className="ph-kpis">
        <div className="ph-kpi" title="Market value of the shares held plus cash set aside for puts, net of what buying the options back would cost">
          <span>Portfolio value</span><b>{money(s?.value)}</b>{missing(s?.value_missing)}
        </div>
        <div className="ph-kpi" title="What your open trades tie up, fixed when they opened: shares at what you paid, puts at strike × shares">
          <span>Capital committed</span><b>{money(s?.capital_committed)}</b>
        </div>
        <div className="ph-kpi">
          <span>{open ? 'Today' : 'Last close'}</span>
          <b className={sign(s?.today_pnl)}>{signed(s?.today_pnl)}</b>{missing(s?.today_pnl_missing)}
          {showLatest && (
            <LatestPriceChip
              className="ph-latest"
              session={lt!.bar_session}
              barTime={lt!.bar_time}
              delayedMinutes={lt!.delayed_minutes}
              pnl={lt!.latest_today_pnl}
              pnlLabel="Today"
              compact
            />
          )}
        </div>
        <div className="ph-kpi"><span>Month to date</span><b className={sign(s?.mtd_pnl)}>{signed(s?.mtd_pnl)}</b></div>
        <div className="ph-kpi" title="Sum of the rows' Total P&L (each the current leg: shares since this option was sold, the option, dividends)">
          <span>Total P&amp;L</span><b className={sign(s?.total_pnl)}>{signed(s?.total_pnl)}</b>{missing(s?.total_pnl_missing)}
        </div>
        <div className="ph-kpi"><span>Theta / day</span><b className={sign(s?.theta_per_day)}>{signed(s?.theta_per_day)}</b></div>
        <div className="ph-kpi" title="Remaining yield: the time value your options still have to earn, annualised on capital committed, weighted by capital committed">
          <span>Avg yield</span><b>{s?.avg_yield != null ? `${(s.avg_yield * 100).toFixed(1)}%` : '—'}</b>
        </div>
        <span className="ph-fresh">
          <i className={open ? 'is-open' : ''} />
          {open ? 'Market open · marks refresh during the day' : `Market closed · as of ${asOf ?? 'last'} close · option marks are last close`}
        </span>
      </div>
      <div className="ph-tools">
        <div className="ph-chips" role="group" aria-label="Filter by what your rules say">
          {chips.map((c) => (
            <button
              key={c.key}
              type="button"
              className={`ph-chip is-${c.key} ${filter === c.key ? 'is-on' : ''}`}
              aria-pressed={filter === c.key}
              disabled={c.n === 0}
              onClick={() => setFilter(filter === c.key ? 'all' : c.key)}
            >
              {c.key !== 'all' && <i />}
              {c.n != null ? `${c.n} ${c.label}` : c.label}
            </button>
          ))}
        </div>
        <input
          className="ph-search"
          type="search"
          placeholder="Search ticker…"
          aria-label="Search positions by ticker"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <PortfolioBriefButton onClick={onBrief} isLoading={briefLoading} />
      </div>
    </div>
  );
};

export default PositionsHeader;
